export const ENTITY_CONDITIONAL_COLUMNS = [
  'diseaseName',
  'geneSymbol',
  'variantStrID',
  'strID',
  'chemPrefName',
  'chemID',
  'chemIDs',
  'chemsInSentence',
] as const;

export const ENTITY_PINNABLE_COLUMNS: string[] = [...ENTITY_CONDITIONAL_COLUMNS];

const CHEMICAL_PIN_COLUMNS = ['chemPrefName', 'chemID', 'chemIDs', 'chemsInSentence'];

export type ColumnVisibilityRecord = Record<string, boolean>;

export function applyEntityColumnDefaults(
  current: ColumnVisibilityRecord | undefined,
  defaults: ColumnVisibilityRecord | undefined,
): ColumnVisibilityRecord | undefined {
  if (!defaults) return current;
  if (!current) return { ...defaults };

  let changed = false;
  const next = { ...current };
  ENTITY_CONDITIONAL_COLUMNS.forEach(col => {
    if (defaults[col] !== undefined && next[col] !== defaults[col]) {
      next[col] = defaults[col];
      changed = true;
    }
  });
  return changed ? next : current;
}

export function getMismatchedEntityColumns(
  controlled: ColumnVisibilityRecord | undefined,
  panelDefaults: ColumnVisibilityRecord,
): string[] {
  return ENTITY_CONDITIONAL_COLUMNS.filter(
    col => panelDefaults[col] !== undefined && controlled?.[col] !== panelDefaults[col],
  );
}

export function getEntityColumnVisibilityDiff(
  previousDefaults: ColumnVisibilityRecord,
  nextDefaults: ColumnVisibilityRecord,
): { becameVisible: string[]; becameHidden: string[] } {
  const becameVisible: string[] = [];
  const becameHidden: string[] = [];

  ENTITY_CONDITIONAL_COLUMNS.forEach(col => {
    const prev = previousDefaults[col];
    const next = nextDefaults[col];
    if (next !== undefined && prev !== next) {
      if (next === true) becameVisible.push(col);
      else becameHidden.push(col);
    }
  });

  return { becameVisible, becameHidden };
}

export function mergeEntityColumnsIntoOrder(
  currentOrder: string[] | undefined,
  defaults: ColumnVisibilityRecord | undefined,
): string[] | undefined {
  if (!defaults) return currentOrder;
  const becomeVisible = ENTITY_CONDITIONAL_COLUMNS.filter(col => defaults[col] === true);
  const becomeVisibleSet = new Set<string>(becomeVisible);
  const becomeHidden = new Set<string>(ENTITY_CONDITIONAL_COLUMNS.filter(col => defaults[col] === false));
  const preserved = (currentOrder ?? []).filter(col => !becomeHidden.has(col) && !becomeVisibleSet.has(col));
  const visibleAlreadyInOrder = becomeVisible.filter(col => (currentOrder ?? []).includes(col));
  const visibleMissing = becomeVisible.filter(col => !visibleAlreadyInOrder.includes(col));
  return [...visibleMissing, ...visibleAlreadyInOrder, ...preserved];
}

export function getEntityColumnsToPin(
  model: ColumnVisibilityRecord | null | undefined,
  options: { view?: string; urlView?: string; tab?: string },
): string[] {
  if (!model) return [];

  const activeView = (options.urlView || options.view || '').toUpperCase();
  const activeTab = (options.tab || '').toUpperCase();
  const isVda = activeTab === 'VDA';

  const columnsByView: Record<string, string[]> = isVda
    ? {
        DISEASES: ['diseaseName', 'variantStrID'],
        GENES: ['geneSymbol', 'variantStrID'],
        VARIANTS: ['variantStrID', 'strID'],
        CHEMICALS: CHEMICAL_PIN_COLUMNS,
      }
    : {
        DISEASES: ['diseaseName', 'geneSymbol'],
        GENES: ['geneSymbol', 'diseaseName'],
        VARIANTS: ['variantStrID', 'strID'],
        CHEMICALS: CHEMICAL_PIN_COLUMNS,
      };

  const candidates = columnsByView[activeView] ?? (isVda ? ['variantStrID', 'strID'] : []);
  return candidates.filter(col => model[col] === true);
}
